import { Citation } from "../../../adapters/insights-agent/state/AppState.ts";
import { CITATION_MARKER_PREFIX, CITATION_MARKER_SUFFIX } from "./constants.ts";

export class CitationMarkerInjector {
  citationMarkerPrefix: string;
  citationMarkerSuffix: string;

  constructor() {
    this.citationMarkerPrefix = CITATION_MARKER_PREFIX;
    this.citationMarkerSuffix = CITATION_MARKER_SUFFIX;
  }

  createCitationMarker = (referenceIds: string[]) =>
    `${this.citationMarkerPrefix}${[...new Set(referenceIds)].join(",")}${this.citationMarkerSuffix}`;

  injectMarkers = (
    text: string,
    citations: Citation[],
    options?: { splitCitations: boolean },
  ): string =>
    this.getReversedCitations(citations).reduce(
      (currentText, citation) =>
        this.processCitation(currentText, citation, options?.splitCitations),
      text,
    );

  private processCitation = (
    text: string,
    citation: Citation,
    shouldSplitCitations?: boolean,
  ): string =>
    shouldSplitCitations
      ? this.processSplitCitation(text, citation)
      : this.injectMarkerAtPosition(
          text,
          citation.referenceIds,
          this.bytePositionToCharPosition(text, citation.pointer),
        );

  private processSplitCitation = (text: string, citation: Citation): string =>
    [...citation.referenceIds]
      .reverse()
      .reduce(
        (currentText, referenceId) =>
          this.injectMarkerAtPosition(
            currentText,
            [referenceId],
            this.bytePositionToCharPosition(text, citation.pointer),
          ),
        text,
      );

  private injectMarkerAtPosition = (
    text: string,
    referenceIds: Citation["referenceIds"],
    charPosition: number,
  ): string => {
    return (
      text.slice(0, charPosition) +
      this.createCitationMarker(referenceIds) +
      text.slice(charPosition)
    );
  };

  /**
   * Converts a byte position in a UTF-8 encoded string to a character position
   * that can be used with string methods like slice().
   *
   * Why this is needed:
   * - In UTF-8 encoding, characters can occupy 1-4 bytes each
   * - JavaScript string indices are based on UTF-16 code units, not UTF-8 bytes
   * - Citation pointers are measured in UTF-8 bytes
   *
   * Algorithm explanation:
   * - Uses binary search (O(log n) complexity) to efficiently find the character position
   * - For each iteration, we check if the byte length of the substring up to the middle point
   *   is less than or greater than our target byte position
   * - We narrow the search range until we find the exact character position
   *
   * Example:
   * - For string "Hello 😊 世界" with a bytePosition of 10:
   *   - "Hello " = 6 bytes (ASCII)
   *   - "😊" = 4 bytes (emoji)
   *   - So bytePosition 10 corresponds to character position 7 (after the emoji)
   *
   * @param text - The string to search within
   * @param bytePosition - The UTF-8 byte position to convert
   * @returns The corresponding character position (index) in the JavaScript string
   */
  private bytePositionToCharPosition = (
    text: string,
    bytePosition: number,
  ): number => {
    // TextEncoder converts JavaScript strings to UTF-8 byte arrays
    const encoder = new TextEncoder();

    // Binary search boundaries (character positions)
    let low = 0;
    let high = text.length;

    // Binary search to find the character position
    while (low < high) {
      // Find the middle character position
      const mid = Math.floor((low + high) / 2);

      // Get the substring up to the middle position
      const slice = text.substring(0, mid);

      // Calculate how many UTF-8 bytes this substring occupies
      const bytes = encoder.encode(slice).length;

      if (bytes < bytePosition) {
        // If the byte count is less than our target, search in the right half
        low = mid + 1;
      } else {
        // If the byte count is greater than or equal to our target, search in the left half
        high = mid;
      }
    }

    // 'low' now contains the character position that corresponds to the byte position
    return low;
  };

  /**
   * Sorts citations by pointer DESCENDING to ensure that inserting markers
   * doesn't invalidate the indices of later markers in the original string.
   */
  private getReversedCitations = (citations: Citation[]): Citation[] =>
    [...citations].sort((a, b) => b.pointer - a.pointer);
}
